import { FC, useCallback, useContext, useMemo, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { CurrencyEnum } from '@spectrocoin/sc-currencies';
import AccountSelect from '../../../../components/AccountSelect/AccountSelect';
import Button, { ButtonType } from '../../../../components/Button/Button';
import MessageBlock, {
	BlockStyle,
	MessageType,
} from '../../../../components/MessageBlock/MessageBlock';
import { getErrorMessageOrDefault } from '../../../../helpers/errorMessageHelper/errorMessageHelper';
import { getCurrenciesData } from '../../../../redux/DonationSlice/DonationSelectors';
import { DonationStage } from '../../../../redux/DonationSlice/DonationSlice';
import { RemoteStatus } from '../../../../redux/SharedTypes';
import TestIds from '../../../../tests/TestIds';
import { comparePayCurrencies } from '../../../../helpers/currencyHelper/currencyHelper';

import styles from './CurrencySelect.module.scss';
import DonationContext from '../../Context/DonationContext';
import FooterActions from '../../../../redux/FooterSlice/FooterActions';
import { useAppDispatch } from '../../../../redux/Store';

const messages = defineMessages({
	payWith: {
		id: 'payments.donate_with',
		defaultMessage: 'Donate with',
	},
	button: {
		id: 'payments.confirm',
		defaultMessage: 'Confirm',
	},
});

const CurrencySelect: FC = () => {
	const dispatch = useAppDispatch();
	const { formatMessage } = useIntl();
	const currencies = useSelector(getCurrenciesData)!;

	const { stage, address, setStage, createAddress } = useContext(DonationContext);
	const { status, error } = address;

	const options = useMemo(
		() =>
			currencies
				.map(({ code, name }) => ({
					currencyCode: code,
					currencyName: name,
					value: code,
				}))
				.sort((a, b) => comparePayCurrencies(a.currencyCode, b.currencyCode)),
		[currencies]
	);

	const [donateCurrency, setDonateCurrency] = useState<CurrencyEnum | null>(
		options[0]?.currencyCode || null
	);

	const createAddressCallback = useCallback(() => {
		if (!donateCurrency) return;
		createAddress(donateCurrency)
			.then(() => {
				setStage(DonationStage.Donate);
			})
			.catch(() => {});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [donateCurrency]);

	if (stage !== DonationStage.CurrencySelect) return null;

	return (
		<div className={styles.container} data-cy={TestIds.DonationCurrencySelect}>
			<h2 className={styles.title}>
				<FormattedMessage {...messages.payWith} />
			</h2>
			{error && (
				<MessageBlock
					withIcon
					style={BlockStyle.Border}
					type={MessageType.Error}
					message={formatMessage(getErrorMessageOrDefault(error))}
				/>
			)}
			<AccountSelect
				options={options}
				value={donateCurrency}
				onChange={(value) => {
					setDonateCurrency(value as CurrencyEnum);
					dispatch(FooterActions.setBuyCurrency(value as CurrencyEnum));
				}}
			/>
			<Button
				data-cy={TestIds.DonationCurrencySelectConfirm}
				className={styles.button}
				type={ButtonType.BUTTON}
				isDisabled={!donateCurrency || status === RemoteStatus.InProgress}
				onClick={createAddressCallback}
			>
				<FormattedMessage {...messages.button} />
			</Button>
		</div>
	);
};

export default CurrencySelect;
